import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Bell, Building2, CheckCircle2, ExternalLink, Eye, RefreshCw, Search, ShieldCheck, Trash2, Users, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/auth/AuthContext";
import { StudentManagement } from "@/pages/StudentManagement";

const STATUS_META = {
  not_submitted: { label: "Belum diisi", tone: "chip-tone-warn" },
  pending: { label: "Menunggu verifikasi", tone: "chip-tone-info" },
  verified: { label: "Terverifikasi", tone: "chip-tone-success" },
  needs_revision: { label: "Perlu diperbaiki", tone: "chip-tone-danger" },
};
const FILTER_ORDER = ["all", "pending", "not_submitted", "needs_revision", "verified"];
const FILTER_LABEL = {
  all: "Semua", pending: "Menunggu", not_submitted: "Belum diisi",
  needs_revision: "Perlu perbaikan", verified: "Terverifikasi",
};

export default function ManajemenPT() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [section, setSection] = useState("profiles");
  const [selectedProfileIds, setSelectedProfileIds] = useState(new Set());
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const load = async () => {
    try {
      const [profiles, rem] = await Promise.all([
        api.get("/pt-profiles"),
        api.get("/admin/reminders"),
      ]);
      setItems(profiles.data);
      setReminders(rem.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal memuat data PT.");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setSelectedProfileIds(new Set()); }, [filter, query]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((it) => {
      if (filter !== "all" && it.status !== filter) return false;
      if (!q) return true;
      return `${it.kodept} ${it.namapt} ${it.username} ${it.nama_pengelola || ""}`.toLowerCase().includes(q);
    });
  }, [items, filter, query]);

  const counts = useMemo(() => {
    const c = { all: items.length, pending: 0, not_submitted: 0, needs_revision: 0, verified: 0 };
    items.forEach((it) => { c[it.status] = (c[it.status] || 0) + 1; });
    return c;
  }, [items]);
  const deletableFiltered = filtered;
  const allFilteredSelected = deletableFiltered.length > 0 && deletableFiltered.every((item) => selectedProfileIds.has(item.pt_id));

  const toggleSelectedProfile = (ptId) => setSelectedProfileIds((current) => {
    const next = new Set(current);
    if (next.has(ptId)) next.delete(ptId); else next.add(ptId);
    return next;
  });
  const toggleAllFiltered = () => setSelectedProfileIds((current) => {
    const next = new Set(current);
    if (allFilteredSelected) deletableFiltered.forEach((item) => next.delete(item.pt_id));
    else deletableFiltered.forEach((item) => next.add(item.pt_id));
    return next;
  });

  const requestDelete = (mode, ids, label) => { setDeleteConfirmation(""); setDeleteRequest({ mode, ids, label }); };
  const confirmDeleteProfiles = async () => {
    if (!deleteRequest?.ids?.length) return;
    setDeleteBusy(true); setError(""); setNotice("");
    try {
      const response = deleteRequest.mode === "single"
        ? await api.delete(`/pt-profiles/${deleteRequest.ids[0]}`)
        : await api.post("/pt-profiles/bulk-delete", { pt_ids: deleteRequest.ids });
      setNotice(`${response.data.deleted} akun PT beserta seluruh data terkait berhasil dihapus permanen.`);
      setDeleteRequest(null); setSelected(null); setSelectedProfileIds(new Set());
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menghapus Data Pengelola.");
    } finally { setDeleteBusy(false); }
  };

  const doVerify = async (pt_id) => {
    setError(""); setNotice("");
    try {
      await api.post(`/pt-profiles/${pt_id}/verify`);
      setNotice("Profil pengelola berhasil diverifikasi.");
      setSelected(null); load();
    } catch (e) { setError(e.response?.data?.detail || "Gagal verifikasi."); }
  };

  const doReject = async (pt_id) => {
    if (!rejectNotes.trim()) { setError("Catatan perbaikan wajib diisi."); return; }
    setError(""); setNotice("");
    try {
      await api.post(`/pt-profiles/${pt_id}/reject`, { revision_notes: rejectNotes.trim() });
      setNotice("Profil PT dikembalikan untuk diperbaiki.");
      setSelected(null); setRejectNotes(""); load();
    } catch (e) { setError(e.response?.data?.detail || "Gagal menolak."); }
  };

  const doResetAll = async () => {
    setError(""); setNotice("");
    try {
      const r = await api.post("/pt-profiles/reset-all");
      setNotice(`${r.data?.updated || 0} PT diminta memperbarui data pengelola.`);
      setConfirmReset(false); load();
    } catch (e) { setError(e.response?.data?.detail || "Gagal memperbarui."); }
  };

  const dismissReminder = async (type) => {
    try {
      await api.post(`/admin/reminders/${type}/dismiss`);
      setReminders((s) => s.filter((r) => r.type !== type));
    } catch (e) { setError(e.response?.data?.detail || "Gagal menutup pengingat."); }
  };

  return (
    <div className="acct-wrap" data-testid="manajemen-pt-page">
      <div className="acct-head">
        <div>
          <div className="eyebrow">MANAJEMEN PT</div>
          <h2 data-testid="mpt-page-title">
            {section === "profiles" ? <><span className="mpt-title-desktop">Data Pengelola KIPK Perguruan Tinggi</span><span className="mpt-title-mobile">Data Pengelola KIPK</span></> : "Data Mahasiswa KIPK"}
          </h2>
          <p className="muted-text" style={{ fontSize: 12, marginTop: 4 }}>Login sebagai <strong>{user?.display_name}</strong> · {user?.role}</p>
        </div>
        {section === "profiles" && <button className="download-button" data-testid="reset-all-btn" onClick={() => setConfirmReset(true)}>
          <RefreshCw size={14} /> Perbarui Data Pengelola
        </button>}
      </div>

      <div className="acct-tabs management-section-tabs" data-testid="mpt-section-tabs">
        <button className={`tab-button ${section === "profiles" ? "active" : ""}`} data-testid="mpt-section-profiles" onClick={() => setSection("profiles")}><ShieldCheck size={14} /> Data Pengelola KIPK Perguruan Tinggi</button>
        <button className={`tab-button ${section === "students" ? "active" : ""}`} data-testid="mpt-section-students" onClick={() => setSection("students")}><Users size={14} /> Data Mahasiswa</button>
      </div>

      {section === "students" ? <StudentManagement /> : <>

      {reminders.filter((r) => r.type === "pengelola_reminder").map((r) => (
        <div key={r.type} className="reminder-banner" data-testid={`reminder-${r.type}`}>
          <Bell size={16} />
          <div style={{ flex: 1 }}>
            <strong>Pengingat siklus {r.cycle === "januari" ? "3 Januari" : r.cycle === "agustus" ? "20 Agustus" : "manual"}:</strong> {" "}
            Sudah waktunya meminta seluruh PT memperbarui Data Pengelola. Klik tombol <strong>"Perbarui Data Pengelola"</strong> di atas untuk memicu.
          </div>
          <button className="icon-button" data-testid={`reminder-dismiss-${r.type}`} aria-label="Tutup" onClick={() => dismissReminder(r.type)} style={{ width: 30, height: 30 }}>
            <X size={14} />
          </button>
        </div>
      ))}

      {notice && <div className="info-banner" data-testid="mpt-notice">✓ {notice}</div>}
      {error && <div className="error-banner" data-testid="mpt-error"><AlertTriangle size={14} /> {error}</div>}

      <div className="acct-tabs" data-testid="mpt-filter-tabs">
        {FILTER_ORDER.map((f) => (
          <button key={f} className={`tab-button ${filter === f ? "active" : ""}`} data-testid={`mpt-filter-${f}`} onClick={() => setFilter(f)}>
            {FILTER_LABEL[f]}
            <span className="tab-count">{counts[f] || 0}</span>
          </button>
        ))}
      </div>

      <div className="acct-actionrow">
        <div className="search-wrap"><Search size={15} /><input data-testid="mpt-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari kode/nama PT atau pengelola…" /></div>
        <div className="acct-actions">
          {selectedProfileIds.size > 0 && <button className="download-button student-delete-button" data-testid="mpt-delete-selected" onClick={() => requestDelete("selected", [...selectedProfileIds], `${selectedProfileIds.size} akun terpilih`)}><Trash2 size={14} /> Hapus {selectedProfileIds.size} Akun</button>}
          {deletableFiltered.length > 0 && <button className="download-button ghost student-delete-button" data-testid="mpt-delete-filtered" onClick={() => requestDelete("filtered", deletableFiltered.map((item) => item.pt_id), `${deletableFiltered.length} akun pada hasil filter`)}><Trash2 size={14} /> Hapus Semua Akun Hasil Filter ({deletableFiltered.length})</button>}
        </div>
      </div>

      <div className="table-wrap">
        <table data-testid="mpt-table">
          <thead>
            <tr>
              <th className="student-select-cell"><input type="checkbox" data-testid="mpt-select-all-filtered" aria-label="Pilih semua akun PT pada hasil filter" disabled={!deletableFiltered.length} checked={allFilteredSelected} onChange={toggleAllFiltered} /></th>
              <th>Kode</th>
              <th>Perguruan Tinggi</th>
              <th>Pengelola</th>
              <th>Status</th>
              <th>Kirim</th>
              <th style={{ width: 280 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.not_submitted;
              return (
                <tr key={it.pt_id} data-testid={`mpt-row-${it.pt_id}`}>
                  <td className="student-select-cell"><input type="checkbox" data-testid={`mpt-select-${it.pt_id}`} aria-label={`Pilih akun PT ${it.namapt}`} checked={selectedProfileIds.has(it.pt_id)} onChange={() => toggleSelectedProfile(it.pt_id)} /></td>
                  <td className="mono">{it.kodept}</td>
                  <td><strong>{it.namapt}</strong><div className="muted-text" style={{ fontSize: 11 }}>@{it.username}</div></td>
                  <td>{it.nama_pengelola ? <><strong>{it.nama_pengelola}</strong><div className="muted-text" style={{ fontSize: 11 }}>{it.jabatan_pengelola}</div></> : <span className="muted-text">—</span>}</td>
                  <td><span className={`folder-chip ${meta.tone}`} style={{ pointerEvents: "none" }}>{meta.label}</span></td>
                  <td className="mono" style={{ fontSize: 11 }}>{it.submitted_at?.slice(0, 10) || "—"}</td>
                  <td className="mpt-row-actions">
                    <button className="folder-chip" data-testid={`mpt-view-${it.pt_id}`} onClick={() => { setSelected(it); setRejectNotes(""); }}>
                      Detail
                    </button>
                    <button className="folder-chip chip-tone-info" data-testid={`mpt-preview-${it.pt_id}`} onClick={() => navigate(`/kipk/pt/${encodeURIComponent(it.pt_id)}`)}>
                      <Eye size={12} /> Lihat sebagai PT
                    </button>
                    <button className="folder-chip danger" data-testid={`mpt-delete-${it.pt_id}`} onClick={() => requestDelete("single", [it.pt_id], `${it.namapt} (${it.kodept})`)}><Trash2 size={12} /> Hapus Akun</button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={7} className="empty-state">Tidak ada data untuk filter ini.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="drawer-backdrop" data-testid="mpt-drawer-backdrop" onClick={() => setSelected(null)}>
          <div className="detail-drawer" data-testid="mpt-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-top">
              <div>
                <div className="eyebrow"><Building2 size={12} /> DETAIL PT</div>
                <h2>{selected.namapt}</h2>
                <p className="muted-text" style={{ fontSize: 12, marginTop: 4 }}>Kode: <span className="mono">{selected.kodept}</span></p>
              </div>
              <button className="icon-button" data-testid="mpt-drawer-close" aria-label="Tutup" onClick={() => setSelected(null)}><X size={18} /></button>
            </div>

            <div className="drawer-status">
              <span>Status</span>
              <span className={`folder-chip ${(STATUS_META[selected.status] || STATUS_META.not_submitted).tone}`} style={{ pointerEvents: "none" }}>
                {(STATUS_META[selected.status] || STATUS_META.not_submitted).label}
              </span>
            </div>

            {selected.status === "not_submitted" ? (
              <p className="muted-text">Perguruan tinggi ini belum mengisi data pengelola.</p>
            ) : (
              <dl>
                <dt>Nama Pengelola</dt><dd data-testid="mpt-detail-nama">{selected.nama_pengelola || "—"}</dd>
                <dt>Jabatan</dt><dd>{selected.jabatan_pengelola || "—"}</dd>
                <dt>Nomor WhatsApp</dt><dd className="mono">{selected.nomor_wa || "—"}</dd>
                <dt>Email</dt><dd className="mono">{selected.email || "—"}</dd>
                <dt>Tautan SK</dt>
                <dd>
                  {selected.sk_link ? (
                    <a href={selected.sk_link} target="_blank" rel="noreferrer" className="folder-chip chip-tone-info" data-testid="mpt-sk-link" style={{ marginTop: 4 }}>
                      <ExternalLink size={12} /> Buka dokumen
                    </a>
                  ) : "—"}
                </dd>
                <dt>Dikirim pada</dt><dd className="mono">{selected.submitted_at?.slice(0, 16).replace("T", " ") || "—"}</dd>
                {selected.verified_at && <><dt>Diverifikasi</dt><dd className="mono">{selected.verified_at?.slice(0, 16).replace("T", " ")} oleh {selected.verified_by}</dd></>}
                {selected.revision_notes && <><dt>Catatan Perbaikan</dt><dd>{selected.revision_notes}</dd></>}
              </dl>
            )}

            {(selected.status === "pending" || selected.status === "verified") && (
              <>
                <div style={{ marginTop: 22, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {selected.status === "pending" && (
                    <button className="download-button" data-testid="mpt-verify" onClick={() => doVerify(selected.pt_id)} style={{ background: "var(--green)", borderColor: "var(--green)", boxShadow: "0 4px 12px rgba(16,185,129,.28)" }}>
                      <CheckCircle2 size={14} /> Setujui / Verifikasi
                    </button>
                  )}
                  {selected.status === "verified" && (
                    <div className="info-banner" style={{ margin: 0, flex: 1 }}>
                      <ShieldCheck size={14} /> Sudah terverifikasi. Anda tetap dapat mengembalikan untuk diperbaiki.
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 14 }}>
                  <label className="acct-form" style={{ display: "block" }}>
                    <span style={{ font: "600 10px var(--font-mono)", color: "var(--muted)", letterSpacing: ".14em", textTransform: "uppercase" }}>Catatan Perbaikan (untuk ditolak)</span>
                    <textarea data-testid="mpt-reject-notes" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} rows={3} style={{ width: "100%", marginTop: 6, border: "1px solid var(--line)", borderRadius: 10, padding: "10px 14px", font: "13px var(--font-body)", background: "#fff", resize: "vertical" }} placeholder="Tulis apa yang harus diperbaiki oleh PT…" />
                  </label>
                  <button className="download-button" data-testid="mpt-reject" onClick={() => doReject(selected.pt_id)} disabled={!rejectNotes.trim()} style={{ marginTop: 10, background: "var(--red)", borderColor: "var(--red)" }}>
                    <AlertTriangle size={14} /> Kembalikan untuk Diperbaiki
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirmReset && (
        <div className="drawer-backdrop login-shell" data-testid="reset-all-backdrop" onClick={() => setConfirmReset(false)}>
          <div className="request-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="icon-button" data-testid="reset-close" aria-label="Tutup" onClick={() => setConfirmReset(false)} style={{ position: "absolute", right: 14, top: 14 }}><X size={18} /></button>
            <div className="request-dialog-icon" style={{ background: "var(--red-soft)", color: "var(--red)" }}><AlertTriangle size={26} /></div>
            <h3>Perbarui Data Pengelola?</h3>
            <p>Semua PT dengan status "Terverifikasi" atau "Menunggu" akan diminta mengisi ulang data pengelola. Mereka tidak dapat mengakses dashboard sampai admin memverifikasi ulang.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="download-button ghost" onClick={() => setConfirmReset(false)} data-testid="reset-cancel" style={{ flex: 1 }}>Batal</button>
              <button className="download-button" onClick={doResetAll} data-testid="reset-confirm" style={{ flex: 1, justifyContent: "center" }}>Ya, Perbarui</button>
            </div>
          </div>
        </div>
      )}
      {deleteRequest && (
        <div className="drawer-backdrop login-shell" data-testid="mpt-delete-backdrop" onClick={deleteBusy ? undefined : () => setDeleteRequest(null)}>
          <div className="request-dialog" data-testid="mpt-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="icon-button" data-testid="mpt-delete-close" aria-label="Tutup" disabled={deleteBusy} onClick={() => setDeleteRequest(null)} style={{ position: "absolute", right: 14, top: 14 }}><X size={18} /></button>
            <div className="request-dialog-icon" style={{ background: "var(--red-soft)", color: "var(--red)" }}><Trash2 size={26} /></div>
            <h3>Hapus Akun PT Secara Permanen?</h3>
            <p><strong>{deleteRequest.label}</strong> akan hilang bersama profil pengelola, mahasiswa, submission dan draft Task, isian pembaruan, serta akses seluruh berkasnya. Tindakan ini tidak dapat dibatalkan. Riwayat audit tetap disimpan.</p>
            {deleteRequest.mode !== "single" && <label className="acct-form" style={{ display: "block", marginTop: 14 }}><span>Ketik HAPUS untuk melanjutkan</span><input data-testid="mpt-delete-phrase" value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} placeholder="HAPUS" autoComplete="off" /></label>}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button className="download-button ghost" data-testid="mpt-delete-cancel" disabled={deleteBusy} onClick={() => setDeleteRequest(null)} style={{ flex: 1 }}>Batal</button>
              <button className="download-button student-delete-button" data-testid="mpt-delete-confirm" disabled={deleteBusy || (deleteRequest.mode !== "single" && deleteConfirmation !== "HAPUS")} onClick={confirmDeleteProfiles} style={{ flex: 1, justifyContent: "center" }}>{deleteBusy ? "Menghapus…" : "Ya, Hapus Permanen"}</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  );
}
